import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_SETTINGS = { snooze: { disabled: [], custom: [], order: [] }, nudge: "toast", categories: [], max_code_len: 24, welcome_dismissed: false };

const clampLen = (n) => {
  const v = parseInt(n, 10);
  if (Number.isNaN(v)) return 24;
  return Math.max(15, Math.min(30, v));
};

const normalize = (s) => ({
  ...DEFAULT_SETTINGS,
  ...s,
  snooze: { disabled: [], custom: [], order: [], ...(s?.snooze || {}) },
  nudge: s?.nudge || "toast",
  categories: s?.categories || [],
  max_code_len: clampLen(s?.max_code_len ?? 24),
  welcome_dismissed: !!s?.welcome_dismissed,
});

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setSettings(normalize(r.data)))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const saveSettings = useCallback(async (next) => {
    const norm = normalize(next);
    setSettings(norm);
    try {
      await api.put("/settings", norm);
    } catch {
      toast.error("Could not save settings");
    }
  }, []);

  const openSettings = useCallback(() => setOpen(true), []);

  const addCategory = (name, color = "slate") => {
    const cat = { id: crypto.randomUUID().slice(0, 8), name: name.trim(), color };
    saveSettings({ ...settings, categories: [...(settings.categories || []), cat] });
    return cat;
  };

  return (
    <SettingsContext.Provider value={{ settings, saveSettings, loaded, open, setOpen, openSettings, addCategory }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
